/* What the reader handed us, as a list of mailboxes to open: files from the
 * picker, files and folders from a drop, and Apple Mail's export format.
 *
 * **Apple Mail exports a FOLDER per mailbox**, `Inbox.mbox/`, with the mail in
 * a file called `mbox` inside and an `Info.plist` beside it. So a file named
 * `mbox` inside a folder ending `.mbox` is that folder's mailbox, named after
 * the folder. Anything else in a folder must end `.mbox` or `.mbx` to count,
 * because a Thunderbird profile or a download folder is full of files that
 * are not mail. A single file chosen directly is always tried: whoever chose
 * it knows what it is.
 */

const MAILBOX_FILE = /\.(mbox|mbx)$/i;

/** A mailbox's display name from a file and the path it came from. */
export function mailboxName(file, path = "") {
  const parts = path.split("/").filter(Boolean);

  if (file.name === "mbox" && parts.length >= 2 && /\.mbox$/i.test(parts[parts.length - 2])) {
    return parts[parts.length - 2].replace(/\.mbox$/i, "");
  }

  return file.name.replace(MAILBOX_FILE, "") || file.name;
}

/**
 * Picks the mailboxes out of files that came with relative paths (a chosen or
 * dropped folder). Returns [{file, name}], in path order.
 */
export function mailboxesInFolder(entries) {
  return entries
    .filter(({ file, path }) => {
      const parts = path.split("/");
      const folder = parts[parts.length - 2] || "";
      return (file.name === "mbox" && /\.mbox$/i.test(folder)) || MAILBOX_FILE.test(file.name);
    })
    .sort((a, b) => a.path.localeCompare(b.path))
    .map(({ file, path }) => ({ file, name: mailboxName(file, path) }));
}

/** From the folder picker: every File carries `webkitRelativePath`. */
export function fromFolderPicker(fileList) {
  return mailboxesInFolder([...fileList].map((file) => ({ file, path: file.webkitRelativePath || file.name })));
}

/** From the file picker: every file chosen is tried. */
export function fromFilePicker(fileList) {
  return [...fileList].map((file) => ({ file, name: mailboxName(file) }));
}

/**
 * From a drop. Files dropped directly are always tried; dropped folders are
 * walked and filtered like a chosen folder. Reading a directory is
 * asynchronous and batched (`readEntries` returns at most 100 at a time in
 * Chrome), hence the loop.
 */
export async function fromDrop(dataTransfer) {
  const direct = [];
  const walked = [];
  const pending = [];

  for (const item of [...(dataTransfer.items || [])]) {
    const entry = item.webkitGetAsEntry?.();
    if (entry?.isDirectory) pending.push(walk(entry, entry.name, walked));
    else if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) direct.push({ file, name: mailboxName(file) });
    }
  }

  if (!dataTransfer.items?.length) {
    for (const file of dataTransfer.files || []) direct.push({ file, name: mailboxName(file) });
  }

  await Promise.all(pending);
  return [...direct, ...mailboxesInFolder(walked)];
}

async function walk(directory, path, out) {
  const reader = directory.createReader();

  for (;;) {
    const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;

    for (const entry of batch) {
      const childPath = `${path}/${entry.name}`;
      if (entry.isDirectory) await walk(entry, childPath, out);
      else out.push({ file: await new Promise((resolve, reject) => entry.file(resolve, reject)), path: childPath });
    }
  }
}

/** Two mailboxes with one name get a number, so the sidebar can tell them apart. */
export function uniqueName(name, taken) {
  if (!taken.has(name)) return name;
  for (let n = 2; ; n++) if (!taken.has(`${name} (${n})`)) return `${name} (${n})`;
}

/** The same file opened twice is the same mailbox: switch to it rather than read it again. */
export function sameFile(a, b) {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}
